import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ListOrdered, Loader2, DownloadCloud } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { DocketTable } from "./DocketTable";

export const normPinakio = (s) => (s || "").trim().replace(/\s+/g, " ").toUpperCase();
export const docketKey = (pinakioName, date) => `${normPinakio(pinakioName)}|${date}`;

const fmt = (d) => { if (!d) return ""; const p = d.split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
const daysFromToday = (iso) => Math.round((new Date(iso) - new Date(new Date().toISOString().split("T")[0])) / 86400000);

/** Κουμπί «Έκθεμα» στα Πινάκια: ανοίγει το αποθηκευμένο έκθεμα SOLON ή το αντλεί άμεσα (δικάσιμοι ≤ 15 ημερών). */
export const DocketButton = ({ pinakioName, pinakioCode, date, entry, onFetched }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [fetching, setFetching] = useState(false);
  const testId = `docket-${pinakioCode}-${date}`;
  const days = daysFromToday(date);

  useEffect(() => {
    if (!open || !entry?.id) return;
    setDetail(null);
    axios.get(`${API}/solon/dockets/${entry.id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης εκθέματος"));
  }, [open, entry?.id]);

  const fetchNow = async () => {
    setFetching(true);
    try {
      const r = await axios.post(`${API}/solon/dockets/fetch`, { hearing_date: date, pinakio_name: pinakioName }, { timeout: 120000 });
      onFetched?.(r.data);
      setDetail(r.data);
      setOpen(true);
      toast.success(`Έκθεμα ${fmt(date)} · ${pinakioName}: ${r.data.line_count} υποθέσεις`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αδυναμία άντλησης εκθέματος από το SOLON");
    } finally { setFetching(false); }
  };

  if (!entry) {
    if (days < 0 || days > 15) return null;
    return (
      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-sky-700 hover:text-sky-900" disabled={fetching} onClick={fetchNow}
              title="Άντληση εκθέματος δικασίμου από το SOLON.GOV (TrackDocket)" data-testid={`${testId}-fetch`}>
        {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DownloadCloud className="w-3.5 h-3.5" />}
        {fetching ? "Άντληση…" : "Άντληση Εκθέματος"}
      </Button>
    );
  }
  return (
    <>
      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-sky-700 hover:text-sky-900 font-semibold" onClick={() => setOpen(true)}
              title={`Έκθεμα SOLON (αντλήθηκε ${entry.fetched_at ? new Date(entry.fetched_at).toLocaleString("el-GR") : ""})`} data-testid={testId}>
        <ListOrdered className="w-3.5 h-3.5" />
        Έκθεμα ({entry.line_count})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListOrdered className="w-5 h-5 text-sky-700" />Έκθεμα SOLON — {pinakioName} — {fmt(date)}</DialogTitle>
            <DialogDescription>
              {entry.line_count} υποθέσεις στο έκθεμα{entry.matched_count ? ` · ${entry.matched_count} στο σύστημα` : ""}
              {days >= 0 && days <= 15 && (
                <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs" disabled={fetching} onClick={fetchNow} data-testid={`${testId}-refresh`}>
                  {fetching ? "Ανανέωση…" : "Ανανέωση από SOLON"}
                </Button>
              )}
            </DialogDescription>
          </DialogHeader>
          <DocketTable detail={detail} testId={testId} />
        </DialogContent>
      </Dialog>
    </>
  );
};
